// Central application logger. Everything that would otherwise reach for `console.*`
// goes through here instead, so logs are leveled, scoped, environment-aware and
// (later) pluggable to a remote sink WITHOUT touching call sites. See docs/logging.md.

export type LogLevel = "debug" | "info" | "warn" | "error";

// Numeric ranking so we can ask "is this level >= the minimum we currently emit?".
const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

// Initial minimum level: an explicit env override wins; otherwise stay quiet in
// production (warnings + errors only) and chatty in development.
function resolveInitialLevel(): LogLevel {
  const fromEnv = process.env.REACT_APP_LOG_LEVEL as LogLevel | undefined;
  if (fromEnv && fromEnv in LEVEL_ORDER) return fromEnv;
  return process.env.NODE_ENV === "production" ? "warn" : "debug";
}

let currentLevel: LogLevel = resolveInitialLevel();

/** Raise/lower verbosity at runtime (e.g. behind a hidden debug switch). */
export function setLogLevel(level: LogLevel) {
  currentLevel = level;
}

export function getLogLevel(): LogLevel {
  return currentLevel;
}

// A single formatted log record. Kept as a real object (not a pre-joined string)
// so a future remote sink can serialize the structured `context`.
export interface LogRecord {
  level: LogLevel;
  scope?: string;
  message: string;
  context?: unknown;
}

// The sink is where records actually go. Console today; swapping in Sentry/Datadog
// later is a one-line change here instead of editing every call site.
function consoleSink({ level, scope, message, context }: LogRecord) {
  // Map our levels onto console methods so warnings/errors keep their semantic
  // channels (and DevTools filtering); debug/info use console.log.
  const method =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : console.log;
  const tag = scope ? `[${scope}]` : "";
  if (context !== undefined) method(`${tag} ${message}`.trim(), context);
  else method(`${tag} ${message}`.trim());
}

let sink: (record: LogRecord) => void = consoleSink;

/** Replace the destination of all logs (e.g. forward to an error-tracking service). */
export function setLogSink(next: (record: LogRecord) => void) {
  sink = next;
}

function emit(
  level: LogLevel,
  scope: string | undefined,
  message: string,
  context?: unknown
) {
  // Gate by level first so suppressed logs cost effectively nothing.
  if (LEVEL_ORDER[level] < LEVEL_ORDER[currentLevel]) return;
  try {
    sink({ level, scope, message, context });
  } catch {
    // Logging must never break a feature, so swallow any sink failure.
  }
}

export interface Logger {
  debug(message: string, context?: unknown): void;
  info(message: string, context?: unknown): void;
  warn(message: string, context?: unknown): void;
  error(message: string, context?: unknown): void;
}

function makeLogger(scope?: string): Logger {
  return {
    debug: (m, c) => emit("debug", scope, m, c),
    info: (m, c) => emit("info", scope, m, c),
    warn: (m, c) => emit("warn", scope, m, c),
    error: (m, c) => emit("error", scope, m, c),
  };
}

/** Root, unscoped logger for one-off use. */
export const logger = makeLogger();

/**
 * Create a logger that tags every line with its source, e.g.
 *   const log = createLogger("GitAccounts");
 *   log.error("disconnect failed", { accountId, err });
 */
export function createLogger(scope: string): Logger {
  return makeLogger(scope);
}
